/**
 * Client-Side Filter Utilities
 * 
 * This module provides filtering functions for search results.
 * Each filter function takes items and filter criteria, returning filtered results.
 */

/**
 * Filter items by merchant
 * @param {Array} items - Array of search result items
 * @param {Array} selectedMerchants - Array of selected merchant names
 * @returns {Array} Filtered items
 */
export const filterByMerchant = (items, selectedMerchants) => {
  if (!selectedMerchants || selectedMerchants.length === 0) {
    return items;
  }

  return items.filter(item => {
    return item.locales.some(locale => {
      const merchantName = locale.merchantName || '';
      return selectedMerchants.some(selectedMerchant => {
        if (selectedMerchant === 'Amazon') {
          return merchantName.toLowerCase().includes('amazon');
        }
        if (selectedMerchant === 'Third Party') {
          return !merchantName.toLowerCase().includes('amazon');
        }
        return merchantName.toLowerCase().includes(selectedMerchant.toLowerCase());
      });
    });
  });
};

/**
 * Filter items by condition
 * @param {Array} items - Array of search result items
 * @param {Array} selectedConditions - Array of selected conditions
 * @returns {Array} Filtered items
 */
export const filterByCondition = (items, selectedConditions) => {
  if (!selectedConditions || selectedConditions.length === 0) {
    return items;
  }

  return items.filter(item => {
    return item.locales.some(locale => {
      const condition = locale.condition || 'New';
      return selectedConditions.some(selectedCondition => 
        condition.toLowerCase() === selectedCondition.toLowerCase()
      );
    });
  });
};

/**
 * Filter items by availability
 * @param {Array} items - Array of search result items
 * @param {Array} selectedAvailability - Array of selected availability options
 * @returns {Array} Filtered items
 */
export const filterByAvailability = (items, selectedAvailability) => {
  if (!selectedAvailability || selectedAvailability.length === 0) {
    return items;
  }

  return items.filter(item => {
    return item.locales.some(locale => {
      const availability = locale.availability || '';
      return selectedAvailability.some(selectedOption => {
        if (selectedOption === 'Now') {
          return availability.toLowerCase().includes('now') || 
                 availability.toLowerCase().includes('available') ||
                 availability === '';
        }
        if (selectedOption === 'Backorderable') {
          return availability.toLowerCase().includes('backorder');
        }
        return availability.toLowerCase().includes(selectedOption.toLowerCase());
      });
    });
  });
};

/**
 * Filter items by price range
 * @param {Array} items - Array of search result items
 * @param {Object} priceRange - Object with min and max price values
 * @returns {Array} Filtered items
 */
export const filterByPriceRange = (items, priceRange) => {
  if (!priceRange || (priceRange.min === null && priceRange.max === null)) {
    return items;
  }

  // Convert string values to numbers for comparison
  const minPrice = priceRange.min !== null ? parseFloat(priceRange.min) : null;
  const maxPrice = priceRange.max !== null ? parseFloat(priceRange.max) : null;

  return items.filter(item => {
    return item.locales.some(locale => {
      const price = locale.priceIls || locale.price || 0;
      const meetsMin = minPrice === null || price >= minPrice;
      const meetsMax = maxPrice === null || price <= maxPrice;
      return meetsMin && meetsMax;
    });
  });
};

/**
 * Filter items by domain (Amazon region)
 * @param {Array} items - Array of search result items
 * @param {Array} selectedDomains - Array of selected domain names
 * @returns {Array} Filtered items
 */
export const filterByDomain = (items, selectedDomains) => {
  if (!selectedDomains || selectedDomains.length === 0) {
    return items;
  }

  return items.filter(item => {
    return item.locales.some(locale => {
      const domain = locale.domain || '';
      return selectedDomains.some(selectedDomain => 
        domain.toLowerCase().includes(selectedDomain.toLowerCase())
      );
    });
  });
};

/**
 * Filter items by delivery options
 * @param {Array} items - Array of search result items
 * @param {Array} selectedDeliveryOptions - Array of selected delivery options
 * @returns {Array} Filtered items
 */
export const filterByDeliveryOptions = (items, selectedDeliveryOptions) => {
  if (!selectedDeliveryOptions || selectedDeliveryOptions.length === 0) {
    return items;
  }

  return items.filter(item => {
    return item.locales.some(locale => {
      const deliveryFlags = locale.deliveryFlags || '';
      return selectedDeliveryOptions.some(selectedOption => {
        if (selectedOption === 'Prime') {
          return deliveryFlags.toLowerCase().includes('prime');
        }
        if (selectedOption === 'FreeShipping') {
          return deliveryFlags.toLowerCase().includes('free') || 
                 deliveryFlags.toLowerCase().includes('shipping');
        }
        return deliveryFlags.toLowerCase().includes(selectedOption.toLowerCase());
      });
    });
  });
};

/**
 * Main filtering function that applies all active filters
 * @param {Array} items - Array of search result items
 * @param {Object} filters - Object containing all filter criteria
 * @returns {Array} Filtered items
 */
export const applyClientFilters = (items, filters) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  let filteredItems = items;

  // Apply each filter sequentially
  if (filters.merchants && filters.merchants.length > 0) {
    filteredItems = filterByMerchant(filteredItems, filters.merchants);
  }

  if (filters.conditions && filters.conditions.length > 0) {
    filteredItems = filterByCondition(filteredItems, filters.conditions);
  }

  if (filters.availability && filters.availability.length > 0) {
    filteredItems = filterByAvailability(filteredItems, filters.availability);
  }

  if (filters.priceRange && (filters.priceRange.min !== null || filters.priceRange.max !== null)) {
    filteredItems = filterByPriceRange(filteredItems, filters.priceRange);
  }

  if (filters.domains && filters.domains.length > 0) {
    filteredItems = filterByDomain(filteredItems, filters.domains);
  }

  if (filters.deliveryOptions && filters.deliveryOptions.length > 0) {
    filteredItems = filterByDeliveryOptions(filteredItems, filters.deliveryOptions);
  }

  return filteredItems;
};

/**
 * Check if any filters are active
 * @param {Object} filters - Object containing all filter criteria
 * @returns {Boolean} True if any filters are active
 */
export const hasActiveFilters = (filters) => {
  if (!filters) return false;

  return (
    (filters.merchants && filters.merchants.length > 0) ||
    (filters.conditions && filters.conditions.length > 0) ||
    (filters.availability && filters.availability.length > 0) ||
    (filters.priceRange && (filters.priceRange.min !== null || filters.priceRange.max !== null)) ||
    (filters.domains && filters.domains.length > 0) ||
    (filters.deliveryOptions && filters.deliveryOptions.length > 0)
  );
};

/**
 * Get count of active filters
 * @param {Object} filters - Object containing all filter criteria
 * @returns {Number} Count of active filters
 */
export const getActiveFilterCount = (filters) => {
  if (!filters) return 0;

  let count = 0;
  
  if (filters.merchants && filters.merchants.length > 0) count++;
  if (filters.conditions && filters.conditions.length > 0) count++;
  if (filters.availability && filters.availability.length > 0) count++;
  if (filters.priceRange && (filters.priceRange.min !== null || filters.priceRange.max !== null)) count++;
  if (filters.domains && filters.domains.length > 0) count++;
  if (filters.deliveryOptions && filters.deliveryOptions.length > 0) count++;

  return count;
};

/**
 * Clear all filters
 * @returns {Object} Empty filter object
 */
export const clearAllFilters = () => ({
  merchants: [],
  conditions: [],
  availability: [],
  priceRange: { min: null, max: null },
  domains: [],
  deliveryOptions: []
});

/**
 * Get available filter options from items
 * @param {Array} items - Array of search result items
 * @returns {Object} Object containing available options for each filter type
 */
export const getAvailableFilterOptions = (items) => {
  if (!items || !Array.isArray(items)) {
    return {
      merchants: [],
      conditions: [],
      availability: [],
      domains: [],
      deliveryOptions: []
    };
  }

  const merchants = new Set();
  const conditions = new Set();
  const availability = new Set();
  const domains = new Set();
  const deliveryOptions = new Set();

  items.forEach(item => {
    if (item.locales && Array.isArray(item.locales)) {
      item.locales.forEach(locale => {
        // Extract merchants
        if (locale.merchantName) {
          const merchantName = locale.merchantName;
          if (merchantName.toLowerCase().includes('amazon')) {
            merchants.add('Amazon');
          } else {
            merchants.add('Third Party');
          }
        }

        // Extract conditions
        if (locale.condition) {
          conditions.add(locale.condition);
        }

        // Extract availability
        if (locale.availability) {
          availability.add(locale.availability);
        }

        // Extract domains
        if (locale.domain) {
          domains.add(locale.domain);
        }

        // Extract delivery options (if available in future updates)
        if (locale.deliveryFlags) {
          if (locale.deliveryFlags.toLowerCase().includes('prime')) {
            deliveryOptions.add('Prime');
          }
          if (locale.deliveryFlags.toLowerCase().includes('free')) {
            deliveryOptions.add('FreeShipping');
          }
        }
      });
    }
  });

  return {
    merchants: Array.from(merchants),
    conditions: Array.from(conditions),
    availability: Array.from(availability),
    domains: Array.from(domains),
    deliveryOptions: Array.from(deliveryOptions)
  };
}; 